import './S_Pagination.scss';
import { TranslationKey } from '@Localization';
import { first, last, range } from 'lodash/fp';
import { ReactElement } from 'react';
import { ChevronLeftIcon } from '../static/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '../static/icons/ChevronRightIcon';
import { FirstPageIcon } from '../static/icons/FirstPageIcon';
import { LastPageIcon } from '../static/icons/LastPageIcon';
import { MoreHorizontalIcon } from '../static/icons/MoreHorizontalIcon';
import { S_PaginationPageButton } from './paginationControls/paginationButtons/S_PaginationPageButton';
import { PaginationItem } from './PaginationItem';
import { PaginationLink } from './PaginationLink';
import { S_PaginationPageSize } from './paginationPageSize/S_PaginationPageSize';
import { S_PaginationResults } from './paginationResults/S_PaginationResults';

export interface S_PaginationProps {
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  onChange: (pageNumber: number, pageSize: number) => void | Promise<void>;
  pageSizeLabel?: TranslationKey;
}

export const S_Pagination = ({ pageNumber, pageSize, totalCount, pageSizeLabel, onChange }: S_PaginationProps): ReactElement => {
  const firstPage: number = 1;
  const pageRangeOffset: number = 3;

  const handleScrollToTop = (): void => {
    const element = document.getElementsByClassName('dashboard-inside')[0];
    if (element) {
      element.scrollTop = 0;
    }
  };

  const handlePageChange = async (pageNumber: number, pageSize: number): Promise<void> => {
    await onChange(pageNumber, pageSize);
    handleScrollToTop();
  };

  const getNormalizedFirstPage = (): number => Math.max(pageNumber, firstPage);
  const getLastPage = (): number => Math.ceil(totalCount / pageSize);
  const getPageRange = (): number[] => {
    const firstVisiblePage = Math.max(firstPage, pageNumber - pageRangeOffset);
    const lastVisiblePage = Math.min(getLastPage(), pageNumber + pageRangeOffset);
    return range(firstVisiblePage, lastVisiblePage + 1);
  };

  const navigate = async (page: number): Promise<void> => {
    if (pageNumber !== page) {
      await handlePageChange(page, pageSize);
    }
  };

  const handleClick = async (page: number): Promise<void> => {
    await navigate(page);
  };
  const handleNextClick = async (): Promise<void> => {
    await navigate(getNormalizedFirstPage() + 1);
  };
  const handlePreviousClick = async (): Promise<void> => {
    await navigate(getNormalizedFirstPage() - 1);
  };
  const handleFirstClick = async (): Promise<void> => {
    await navigate(firstPage);
  };
  const handleLastClick = async (): Promise<void> => {
    await navigate(getLastPage());
  };
  const handlePageSizeChange = async (newPageSize: number): Promise<void> => {
    if (newPageSize !== pageSize) {
      let newPageNumber = 1;
      if (pageNumber !== 1) {
        const lastVisibleElementIndex = Math.min(pageNumber * pageSize, totalCount);
        newPageNumber = Math.ceil(lastVisibleElementIndex / newPageSize);
      }
      await handlePageChange(newPageNumber, newPageSize);
    }
  };

  if (totalCount === 0) {
    return <></>;
  }

  const pageCount = getLastPage();
  const pageRange = getPageRange();

  const disableForwardNavigation = getNormalizedFirstPage() === pageCount;
  const disableBackwardNavigation = getNormalizedFirstPage() === firstPage;

  return (
    <div
      id='acdl-pagination'
      className='pagination'
    >
      <S_PaginationResults
        totalCount={totalCount}
        pageNumber={getNormalizedFirstPage()}
        pageSize={pageSize}
      />
      <div className='pagination-navigation-container'>
        <PaginationItem disabled={disableBackwardNavigation}>
          <PaginationLink onClick={handleFirstClick}>
            <FirstPageIcon />
          </PaginationLink>
        </PaginationItem>
        <PaginationItem disabled={disableBackwardNavigation}>
          <PaginationLink onClick={handlePreviousClick}>
            <ChevronLeftIcon />
          </PaginationLink>
        </PaginationItem>
        {first(pageRange) !== firstPage && (
          <div className='page-item'>
            <MoreHorizontalIcon />
          </div>
        )}
        {pageRange.map((p) => {
          return (
            <PaginationItem
              key={p}
              active={getNormalizedFirstPage() === p}
            >
              <S_PaginationPageButton
                page={p}
                onClick={handleClick}
              />
            </PaginationItem>
          );
        })}
        {last(pageRange) !== pageCount && (
          <PaginationItem>
            <MoreHorizontalIcon />
          </PaginationItem>
        )}
        <PaginationItem disabled={disableForwardNavigation}>
          <PaginationLink onClick={handleNextClick}>
            <ChevronRightIcon />
          </PaginationLink>
        </PaginationItem>
        <PaginationItem disabled={disableForwardNavigation}>
          <PaginationLink onClick={handleLastClick}>
            <LastPageIcon />
          </PaginationLink>
        </PaginationItem>
      </div>
      <S_PaginationPageSize
        onChange={handlePageSizeChange}
        pageSize={pageSize}
        pageSizeLabel={pageSizeLabel}
      />
    </div>
  );
};
